/**
 * Client-side storage for M1/M2/M3 engagement (023.02, 023.05).
 * Versioned payloads so future schema changes can be migrated; unknown versions ignored on read.
 * Only access in browser (useEffect / client components).
 *
 * localStorage-only by design (LA-4):
 * All course progress lives exclusively in the browser. Users who clear browser
 * storage, switch devices, or use private browsing lose their progress.
 * This is an accepted trade-off for a free, short (18-lesson) course where:
 *   - Progress loss is annoying but not high-stakes (no paid certificate).
 *   - Most users complete the course on a single device in a few sessions.
 *   - Server-side sync would require a new endpoint, a DB collection, and a
 *     real auth mechanism (the signup cookie is not a secure session token).
 * If user-reported progress loss becomes a support issue, consider a lightweight
 * PUT /api/free-vhf-progress endpoint backed by the signup email as identifier.
 */

export const STORAGE_VERSION = 1;

const STORAGE_KEY = "freeVhfSelfReport_v1";

export const UNDERSTAND_VALUES = [
  "almost-everything",
  "some-parts",
  "very-little",
] as const;
export const COMFORTABLE_VALUES = ["yes", "no"] as const;

export type UnderstandValue = (typeof UNDERSTAND_VALUES)[number];
export type ComfortableValue = (typeof COMFORTABLE_VALUES)[number];

export interface SelfReportAnswer {
  understand?: UnderstandValue;
  comfortable?: ComfortableValue;
}

export type SelfReportAnswers = Record<string, SelfReportAnswer>;

function readVersioned<T>(
  raw: string | null,
  fallback: T
): T {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    // Envelope check (manual to avoid Zod in client)
    if (parsed && typeof parsed === "object" && parsed.version === STORAGE_VERSION) {
      return (parsed.data as T) || fallback;
    }
    return (parsed as T) || fallback;
  } catch {
    return fallback;
  }
}

function writeVersioned<T>(data: T): string {
  return JSON.stringify({ version: STORAGE_VERSION, data });
}

/** True if localStorage exists and a test write/remove succeeds. */
export function isStorageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const key = "__freeVhf_storage_test__";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** "unavailable" | "quota" when storage fails; null when ok. */
export function getStorageError(): "unavailable" | "quota" | null {
  if (typeof window === "undefined") return "unavailable";
  try {
    const key = "__freeVhf_storage_test__";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    return null;
  } catch (e) {
    const name = e instanceof Error ? e.name : "";
    return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" ? "quota" : "unavailable";
  }
}

const STORAGE_KEY_LEGACY = "freeVhfSelfReport";

/**
 * Read all stored M1 self-report answers from localStorage.
 * Returns {} when not in browser or on parse error. Supports versioned and legacy payloads.
 */
export function getFreeVhfSelfReportAnswers(): SelfReportAnswers {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(STORAGE_KEY) ?? window.localStorage.getItem(STORAGE_KEY_LEGACY);
  return readVersioned<SelfReportAnswers>(raw, {});
}

export type PersistQuestion = "understand" | "comfortable";

/** Only M1 scenario ids (M1-S01 … M1-S07) are persisted for engagement stats. Single source of truth for stats iteration and total. */
export const M1_SCENARIO_IDS = [
  "M1-S01",
  "M1-S02",
  "M1-S03",
  "M1-S04",
  "M1-S05",
  "M1-S06",
  "M1-S07",
] as const;

const M1_SCENARIO_ID_SET = new Set(M1_SCENARIO_IDS as readonly string[]);

/** Total M1 lessons; use for stats "X of 7". Derived from M1_SCENARIO_IDS. */
export const M1_SCENARIO_COUNT = M1_SCENARIO_IDS.length;

/**
 * Persist a single self-report answer. Only runs in browser.
 * Only persists for M1 scenario ids and if value is in the allowed enum for that question; otherwise no-op.
 */
export function setFreeVhfSelfReportAnswer(
  scenarioId: string,
  question: PersistQuestion,
  value: string
): void {
  if (typeof window === "undefined") return;
  if (!M1_SCENARIO_ID_SET.has(scenarioId)) return;

  const current = getFreeVhfSelfReportAnswers();
  const entry = { ...current[scenarioId] };

  if (question === "understand") {
    if (!(UNDERSTAND_VALUES as readonly string[]).includes(value)) return;
    entry.understand = value as UnderstandValue;
  } else {
    if (!(COMFORTABLE_VALUES as readonly string[]).includes(value)) return;
    entry.comfortable = value as ComfortableValue;
  }

  current[scenarioId] = entry;
  try {
    window.localStorage.setItem(STORAGE_KEY, writeVersioned(current));
  } catch {
    try {
      window.sessionStorage.setItem("freeVhfStorageFull", "1");
    } catch {
      // ignore
    }
  }
}

// --- M2 (Safety & Urgency): who has problem + how clear (023.02) ---

const M2_STORAGE_KEY = "freeVhfSelfReportM2_v1";
export const M2_SCENARIO_IDS = [
  "M2-S01", "M2-S02", "M2-S03", "M2-S04", "M2-S05", "M2-S06", "M2-S07",
] as const;
export const M2_SCENARIO_COUNT = M2_SCENARIO_IDS.length;

const M2_SCENARIO_ID_SET = new Set(M2_SCENARIO_IDS as readonly string[]);

export type M2Question = "who" | "clear";

export interface M2Answer {
  who?: string;
  clear?: string;
}

export type M2Answers = Record<string, M2Answer>;

const M2_STORAGE_KEY_LEGACY = "freeVhfSelfReportM2";

/**
 * Read all stored M2 self-report answers from localStorage.
 */
export function getFreeVhfM2Answers(): M2Answers {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(M2_STORAGE_KEY) ?? window.localStorage.getItem(M2_STORAGE_KEY_LEGACY);
  return readVersioned<M2Answers>(raw, {});
}

/**
 * Persist a single M2 answer. value for "who" can be comma-separated (multiple selection).
 */
export function setFreeVhfM2Answer(
  scenarioId: string,
  question: M2Question,
  value: string
): void {
  if (typeof window === "undefined") return;
  if (!M2_SCENARIO_ID_SET.has(scenarioId)) return;
  const current = getFreeVhfM2Answers();
  const entry = { ...current[scenarioId] };
  if (question === "who") {
    entry.who = value;
  } else {
    entry.clear = value;
  }
  current[scenarioId] = entry;
  try {
    window.localStorage.setItem(M2_STORAGE_KEY, writeVersioned(current));
  } catch {
    try {
      window.sessionStorage.setItem("freeVhfStorageFull", "1");
    } catch {
      // ignore
    }
  }
}

// --- M3 (Can You React?): attempted + confidence (023.02) ---

const M3_STORAGE_KEY = "freeVhfM3Attempt_v1";
/** M3 scenario ids for stats "X of 4 attempted". */
export const M3_SCENARIO_IDS = ["M3-S01", "M3-S02", "M3-S03", "M3-S04"] as const;
export const M3_SCENARIO_COUNT = M3_SCENARIO_IDS.length;

const M3_SCENARIO_ID_SET = new Set(M3_SCENARIO_IDS as readonly string[]);

export interface M3AttemptData {
  attempted?: boolean;
  confidence?: string;
}

export type M3Attempts = Record<string, M3AttemptData>;

const M3_STORAGE_KEY_LEGACY = "freeVhfM3Attempt";

/**
 * Read all stored M3 attempt/confidence data from localStorage.
 */
export function getFreeVhfM3Attempts(): M3Attempts {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(M3_STORAGE_KEY) ?? window.localStorage.getItem(M3_STORAGE_KEY_LEGACY);
  return readVersioned<M3Attempts>(raw, {});
}

/**
 * Persist M3 attempt and/or confidence. Merges with existing entry for that scenario.
 */
export function setFreeVhfM3Attempt(
  scenarioId: string,
  data: M3AttemptData
): void {
  if (typeof window === "undefined") return;
  if (!M3_SCENARIO_ID_SET.has(scenarioId)) return;
  const current = getFreeVhfM3Attempts();
  const existing = current[scenarioId] ?? {};
  const merged: M3AttemptData = {
    ...existing,
    ...(data.attempted !== undefined && { attempted: data.attempted }),
    ...(data.confidence !== undefined && { confidence: data.confidence }),
  };
  current[scenarioId] = merged;
  try {
    window.localStorage.setItem(M3_STORAGE_KEY, writeVersioned(current));
  } catch {
    try {
      window.sessionStorage.setItem("freeVhfStorageFull", "1");
    } catch {
      // ignore
    }
  }
}
